import React, { useEffect, useState, useCallback, useRef } from "react";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import MobileActionMenu from "../../components/MobileActionMenu";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import IconButton from "@material-ui/core/IconButton";
import Typography from "@material-ui/core/Typography";
import Tooltip from "@material-ui/core/Tooltip";
import Chip from "@material-ui/core/Chip";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Dialog from "@material-ui/core/Dialog";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import DialogContentText from "@material-ui/core/DialogContentText";
import DialogActions from "@material-ui/core/DialogActions";
import CircularProgress from "@material-ui/core/CircularProgress";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import ShareIcon from "@mui/icons-material/Share";
import BarChartIcon from "@mui/icons-material/BarChart";
import DownloadIcon from "@mui/icons-material/Download";
import FileUploadIcon from "@mui/icons-material/FileUpload";
import SearchIcon from "@mui/icons-material/Search";
import PublishIcon from "@mui/icons-material/Publish";
import UnpublishedIcon from "@mui/icons-material/Unpublished";
import AssignmentIcon from "@mui/icons-material/Assignment";
import { toast } from "react-toastify";

import {
  listQuizzes,
  createQuiz,
  deleteQuiz,
  updateQuiz,
  exportQuiz,
  importQuiz,
} from "../../services/quizService";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: "24px 24px 0",
    height: "calc(100vh - 64px)",
    display: "flex",
    flexDirection: "column",
    background: "#f8fafc",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 24,
    flexWrap: "wrap",
    gap: 12,
    [theme.breakpoints.down("sm")]: { flexDirection: "column", alignItems: "stretch", marginBottom: 16 },
  },
  headerLeft: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  headerIcon: {
    width: 42,
    height: 42,
    borderRadius: 10,
    background: theme.palette.primary.main,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#fff",
  },
  headerRight: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    [theme.breakpoints.down("sm")]: { flexDirection: "column", alignItems: "stretch" },
  },
  searchField: {
    background: "#fff",
    borderRadius: 8,
    "& .MuiOutlinedInput-root": {
      borderRadius: 8,
      height: 38,
      fontSize: 14,
    },
    [theme.breakpoints.down("sm")]: { width: "100%" },
  },
  tableWrapper: {
    flex: 1,
    background: "#fff",
    borderRadius: 12,
    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
    overflow: "hidden",
    display: "flex",
    flexDirection: "column",
    marginBottom: 24,
    [theme.breakpoints.down("sm")]: { overflowX: "auto" },
  },
  tableScroll: {
    overflowY: "auto",
    flex: 1,
  },
  tableHead: {
    "& th": {
      backgroundColor: "var(--sidebar-color, #1e293b)",
      color: "#cbd5e1",
      fontWeight: 600,
      fontSize: 12,
      textTransform: "uppercase",
      letterSpacing: "0.05em",
      padding: "12px 16px",
      borderBottom: "none",
      whiteSpace: "nowrap",
    },
  },
  tableBody: {
    "& td": {
      borderBottom: "1px solid #f1f5f9",
      padding: "10px 16px",
      fontSize: 13,
      color: "#374151",
    },
    "& tr:hover td": {
      backgroundColor: "#f8fafc",
    },
    "& tr:last-child td": {
      borderBottom: "none",
    },
  },
  emptyState: {
    textAlign: "center",
    padding: "60px 24px",
    color: "#94a3b8",
  },
}));

export default function QuizList() {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const history = useHistory();
  const sidebarColor = theme.palette.primary.main;
  const fileInputRef = useRef(null);

  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [renamingId, setRenamingId] = useState(null);
  const [renamingName, setRenamingName] = useState("");
  const [newFormOpen, setNewFormOpen] = useState(false);
  const [newFormName, setNewFormName] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listQuizzes();
      setQuizzes(data);
    } catch {
      toast.error("Erro ao carregar formulários");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleNew = () => {
    setNewFormName("");
    setNewFormOpen(true);
  };

  const handleCreateConfirm = async () => {
    const name = newFormName.trim() || "Novo Formulário";
    setCreating(true);
    try {
      const quiz = await createQuiz({ name });
      setNewFormOpen(false);
      history.push(`/captacao/quiz/${quiz.id}/editar`);
    } catch {
      toast.error("Erro ao criar formulário");
    } finally {
      setCreating(false);
    }
  };

  const handleTogglePublish = async (quiz) => {
    const newStatus = quiz.status === "published" ? "draft" : "published";
    try {
      await updateQuiz(quiz.id, { status: newStatus });
      setQuizzes(prev => prev.map(q => q.id === quiz.id ? { ...q, status: newStatus } : q));
      toast.success(newStatus === "published" ? "Publicado!" : "Despublicado");
    } catch {
      toast.error("Erro ao alterar status");
    }
  };

  const handleRenameStart = (e, quiz) => {
    e.stopPropagation();
    setRenamingId(quiz.id);
    setRenamingName(quiz.name);
  };

  const handleRenameSave = async (quizId) => {
    const name = renamingName.trim();
    setRenamingId(null);
    if (!name) return;
    try {
      await updateQuiz(quizId, { name });
      setQuizzes(prev => prev.map(q => q.id === quizId ? { ...q, name } : q));
    } catch {
      toast.error("Erro ao renomear formulário");
    }
  };

  const handleRenameKeyDown = (e, quizId) => {
    if (e.key === "Enter") handleRenameSave(quizId);
    if (e.key === "Escape") setRenamingId(null);
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await deleteQuiz(confirmDelete.id);
      setQuizzes(prev => prev.filter(q => q.id !== confirmDelete.id));
      toast.success("Formulário excluído");
      setConfirmDelete(null);
    } catch {
      toast.error("Erro ao excluir");
    } finally {
      setDeleting(false);
    }
  };

  const handleExport = async (quiz) => {
    try {
      const data = await exportQuiz(quiz.id);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `quiz-${quiz.slug}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Erro ao exportar");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await file.text();
      const data = JSON.parse(text);
      await importQuiz(data);
      toast.success("Importado com sucesso!");
      load();
    } catch {
      toast.error("Arquivo inválido");
    } finally {
      e.target.value = "";
    }
  };

  const handleShare = (quiz) => {
    const url = `${window.location.origin}/q/${quiz.slug}`;
    navigator.clipboard.writeText(url);
    toast.success("Link copiado!");
  };

  const filtered = quizzes.filter(q =>
    q.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Box className={classes.root} style={{ "--sidebar-color": sidebarColor }}>
      {/* Header */}
      <Box className={classes.header}>
        <Box className={classes.headerLeft}>
          <Box className={classes.headerIcon}>
            <AssignmentIcon style={{ fontSize: 22 }} />
          </Box>
          <Box>
            <Typography style={{ fontWeight: 600, fontSize: 18, color: "#1e293b", lineHeight: 1.2 }}>
              Quiz &amp; Formulários
            </Typography>
            <Typography style={{ fontSize: 13, color: "#64748b" }}>
              Formulários de captação de leads
            </Typography>
          </Box>
        </Box>
        <Box className={classes.headerRight}>
          <TextField
            className={classes.searchField}
            size="small"
            variant="outlined"
            placeholder="Buscar formulário..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon style={{ fontSize: 18, color: "#94a3b8" }} />
                </InputAdornment>
              ),
            }}
          />
          <Tooltip title="Importar JSON">
            <IconButton size="small" onClick={() => fileInputRef.current?.click()}
              style={{ background: "#f1f5f9", borderRadius: 8 }}>
              <FileUploadIcon style={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <input ref={fileInputRef} type="file" accept=".json" style={{ display: "none" }} onChange={handleImport} />
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleNew}
            style={{ borderRadius: 8, textTransform: "none", fontWeight: 600, height: 38 }}
          >
            Novo Formulário
          </Button>
        </Box>
      </Box>

      {/* Tabela */}
      <Box className={classes.tableWrapper}>
        <Box className={classes.tableScroll}>
          <Table size="small" stickyHeader>
            <TableHead className={classes.tableHead}>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Link / Slug</TableCell>
                <TableCell align="center">Status</TableCell>
                <TableCell align="center">Etapas</TableCell>
                <TableCell align="center">Respostas</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={classes.tableBody}>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" style={{ padding: 40 }}>
                    <CircularProgress size={28} />
                  </TableCell>
                </TableRow>
              ) : filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Box className={classes.emptyState}>
                      <AssignmentIcon style={{ fontSize: 56, color: "#e2e8f0", marginBottom: 12 }} />
                      <Typography variant="h6" style={{ color: "#94a3b8", fontWeight: 500 }}>
                        {search ? "Nenhum formulário encontrado" : "Nenhum formulário criado"}
                      </Typography>
                      {!search && (
                        <Typography variant="body2" style={{ color: "#cbd5e1", marginTop: 4 }}>
                          Clique em "Novo Formulário" para começar
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((quiz) => (
                  <TableRow key={quiz.id} style={{ cursor: "pointer" }}
                    onClick={() => history.push(`/captacao/quiz/${quiz.id}/editar`)}>
                    <TableCell style={{ fontWeight: 500, color: "#1e293b" }} onClick={(e) => e.stopPropagation()}>
                      {renamingId === quiz.id ? (
                        <input
                          autoFocus
                          value={renamingName}
                          onChange={(e) => setRenamingName(e.target.value)}
                          onBlur={() => handleRenameSave(quiz.id)}
                          onKeyDown={(e) => handleRenameKeyDown(e, quiz.id)}
                          onClick={(e) => e.stopPropagation()}
                          style={{
                            border: "none",
                            borderBottom: "2px solid #1976d2",
                            outline: "none",
                            background: "transparent",
                            fontWeight: 500,
                            fontSize: "inherit",
                            color: "#1e293b",
                            width: "100%",
                            padding: "2px 0",
                            fontFamily: "inherit",
                          }}
                        />
                      ) : (
                        <span
                          title="Clique para renomear"
                          style={{ cursor: "text" }}
                          onClick={(e) => handleRenameStart(e, quiz)}
                        >
                          {quiz.name}
                        </span>
                      )}
                    </TableCell>
                    <TableCell style={{ color: "#64748b", fontFamily: "monospace", fontSize: 12 }}>
                      /q/{quiz.slug}
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        size="small"
                        label={quiz.status === "published" ? "Publicado" : "Rascunho"}
                        style={{
                          height: 22,
                          fontSize: 11,
                          fontWeight: 600,
                          background: quiz.status === "published" ? "#dcfce7" : "#f1f5f9",
                          color: quiz.status === "published" ? "#166534" : "#64748b",
                        }}
                      />
                    </TableCell>
                    <TableCell align="center" style={{ color: "#64748b" }}>
                      {quiz.steps?.length ?? 0}
                    </TableCell>
                    <TableCell align="center" style={{ color: "#64748b" }}>
                      {quiz.responseCount ?? 0}
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      {isMobile ? (
                        <MobileActionMenu actions={[
                          { label: "Copiar link", icon: <ShareIcon style={{ fontSize: 17 }} />, onClick: () => handleShare(quiz) },
                          { label: "Renomear", icon: <EditIcon style={{ fontSize: 17 }} />, onClick: () => { setRenamingId(quiz.id); setRenamingName(quiz.name); } },
                          { label: "Ver respostas", icon: <BarChartIcon style={{ fontSize: 17 }} />, onClick: () => history.push(`/captacao/quiz/${quiz.id}/respostas`) },
                          { label: "Exportar JSON", icon: <DownloadIcon style={{ fontSize: 17 }} />, onClick: () => handleExport(quiz) },
                          { label: "Editar", icon: <EditIcon style={{ fontSize: 17 }} />, onClick: () => history.push(`/captacao/quiz/${quiz.id}/editar`) },
                          { label: quiz.status === "published" ? "Despublicar" : "Publicar", icon: quiz.status === "published" ? <UnpublishedIcon style={{ fontSize: 17 }} /> : <PublishIcon style={{ fontSize: 17 }} />, onClick: () => handleTogglePublish(quiz) },
                          { label: "Excluir", icon: <DeleteIcon style={{ fontSize: 17 }} />, onClick: () => setConfirmDelete(quiz), color: "#ef4444" },
                        ]} />
                      ) : (
                        <Box display="flex" justifyContent="center" gap={0.5}>
                          <Tooltip title="Copiar link">
                            <IconButton size="small" onClick={() => handleShare(quiz)}>
                              <ShareIcon style={{ fontSize: 17, color: "#64748b" }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Ver respostas">
                            <IconButton size="small" onClick={() => history.push(`/captacao/quiz/${quiz.id}/respostas`)}>
                              <BarChartIcon style={{ fontSize: 17, color: "#64748b" }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Exportar JSON">
                            <IconButton size="small" onClick={() => handleExport(quiz)}>
                              <DownloadIcon style={{ fontSize: 17, color: "#64748b" }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Editar">
                            <IconButton size="small" onClick={() => history.push(`/captacao/quiz/${quiz.id}/editar`)}>
                              <EditIcon style={{ fontSize: 17, color: "#1976d2" }} />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title={quiz.status === "published" ? "Despublicar" : "Publicar"}>
                            <IconButton size="small" onClick={() => handleTogglePublish(quiz)}>
                              {quiz.status === "published"
                                ? <UnpublishedIcon style={{ fontSize: 17, color: "#f59e0b" }} />
                                : <PublishIcon style={{ fontSize: 17, color: "#22c55e" }} />
                              }
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Excluir">
                            <IconButton size="small" onClick={() => setConfirmDelete(quiz)}>
                              <DeleteIcon style={{ fontSize: 17, color: "#ef4444" }} />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
      </Box>

      {/* Dialog de nome do novo formulário */}
      <Dialog open={newFormOpen} onClose={() => setNewFormOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Novo Formulário</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label="Nome do formulário"
            variant="outlined"
            size="small"
            value={newFormName}
            onChange={(e) => setNewFormName(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleCreateConfirm(); }}
            style={{ marginTop: 8 }}
            placeholder="Ex: Captação de leads, Pesquisa de satisfação..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewFormOpen(false)} disabled={creating}>Cancelar</Button>
          <Button onClick={handleCreateConfirm} color="primary" variant="contained" disabled={creating}>
            {creating ? <CircularProgress size={18} /> : "Criar"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de confirmação de exclusão */}
      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Excluir Formulário</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir <strong>{confirmDelete?.name}</strong>? Esta ação não pode ser desfeita.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(null)} disabled={deleting}>Cancelar</Button>
          <Button onClick={handleDelete} color="secondary" variant="contained" disabled={deleting}>
            {deleting ? <CircularProgress size={18} /> : "Excluir"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
